import { useCallback, useMemo, useState } from "react";
import { ComboBoxItem } from "@/utils/helper.util";
import { NaceIndustryDto } from "@advanced-sales-technologies/backend-client/models/NaceIndustryDto";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { HiOutlineTruck } from "react-icons/hi2";

import { useGetAllNaceIndustries } from "@/hooks/api/search/useGetAllNaceIndustries";
import Button from "@/components/atoms/Button";
import FeaturedIcon from "@/components/atoms/FeaturedIcon";
import BasicModalContent from "@/components/atoms/modal-panels/BasicModalContent";
import NACEIndustryRenderOption, {
  ProcessedIndustryData,
} from "@/components/organisms/filter/industry/NACEIndustryRenderOption";
import SelectAsyncComboBox from "@/components/organisms/SelectAsyncComboBox";

type Props = {
  onClose: (value: boolean) => void;
  preventClose?: boolean;
  onSuccess: (selected: ComboBoxItem[]) => void;
  formName: string;
};

const IndustryAllModal = ({ onClose, preventClose = false, formName, onSuccess }: Props) => {
  const { t } = useTranslation();
  const { setValue, resetField } = useFormContext();
  const values: ComboBoxItem[] = useWatch({ name: formName }) || [];
  const [query, setQuery] = useState("");

  const includedField = `${formName}MainIds`;

  const { data: industries = [], isLoading: isLoadingIndustries } = useGetAllNaceIndustries({ query });

  const industryData = useMemo(() => processIndustryData(industries), [industries]);
  const selectedIds = useMemo(() => new Set(values.map((v) => v.id)), [values]);

  const handleSelectionChange = useCallback(
    (ids: string[], add: boolean) => {
      let newValues: ComboBoxItem[];
      if (add) {
        const toAdd = ids
          .filter((id) => !selectedIds.has(id))
          .map((id) => ({
            id,
            name: industryData.titleMap.get(id)!,
          }));
        newValues = [...values, ...toAdd];
      } else {
        newValues = values.filter((item) => !ids.includes(item.id));
      }
      setValue(formName, newValues);

      // Get Main IDs for the new selection
      const includedMainIds = newValues.map((v) => v.id).filter((mid): mid is string => mid !== null);
      setValue(includedField, includedMainIds);
    },
    [formName, values, selectedIds, industryData, industries, setValue],
  );

  const handleClear = useCallback(() => {
    setValue(formName, []);
    resetField(formName);
    setValue(includedField, []);
    resetField(includedField);
  }, [formName, resetField, setValue]);

  const applyChanges = useCallback(() => {
    onSuccess(values);
    onClose(false);
  }, [onClose, onSuccess, values]);

  return (
    <BasicModalContent
      featuredIcon={<FeaturedIcon icon={HiOutlineTruck} size={"lg"} color={"primary"} />}
      heading={t("search.filter.industry.advanced.title")}
      preventClose={preventClose}
      body={t("search.filter.industry.advanced.description")}
      onClose={onClose}
      data-testid={"all-industries-modal"}
    >
      <SelectAsyncComboBox
        options={industries}
        onQueryUpdate={setQuery}
        label={t("static.modalAdd.search")}
        placeholder={t("search.filter.industry.advanced.placeholder")}
        isLoading={isLoadingIndustries}
        optionsClassName={"flex flex-col pt-3 overflow-y-auto h-96 max-h-full"}
        renderLoading={Array.from({ length: 7 }).map((_, index) => (
          <IsLoading key={index} />
        ))}
        renderOption={(child) => (
          <NACEIndustryRenderOption
            key={child.id}
            option={child}
            selectedIds={selectedIds}
            industryData={industryData}
            onSelectionChange={handleSelectionChange}
          />
        )}
      />

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Button type={"button"} label={t("cancel")} onClick={() => onClose(false)} color="natural" variants="outline" />
        <Button
          label={`${t("search.filter.industry.advanced.deselect")} (${values.length})`}
          variants={"secondary"}
          color={"error"}
          onClick={handleClear}
        />
        <Button
          type={"button"}
          onClick={applyChanges}
          label={t("search.filter.industry.advanced.button")}
          color="brand"
          variants="primary"
        />
      </div>
    </BasicModalContent>
  );
};

const IsLoading = () => {
  return (
    <div className={"flex animate-pulse items-center gap-2 pb-2"}>
      <div className={"size-5 rounded bg-gray-300"} />
      <div className={"h-3 w-24 rounded-full bg-gray-300"} />
    </div>
  );
};

export default IndustryAllModal;

// Process the entire industry tree only when it changes
const processIndustryData = (industries: NaceIndustryDto[]): ProcessedIndustryData => {
  const descendantIds: Record<string, string[]> = {};
  const titleMap = new Map<string, string>();
  const mainIdMap = new Map<string, string | null>();

  const calculateDescendants = (node: NaceIndustryDto): string[] => {
    if (descendantIds[node.id]) {
      return descendantIds[node.id];
    }

    titleMap.set(node.id, node.title);
    mainIdMap.set(node.id, node.main_id);

    const ids: string[] = [];
    for (const child of node.children) {
      ids.push(child.id);
      ids.push(...calculateDescendants(child));
    }

    descendantIds[node.id] = ids;
    return ids;
  };

  // Process each top-level industry
  industries.forEach((node) => {
    calculateDescendants(node);
  });

  return { descendantIds, titleMap, mainIdMap };
};
